import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

export class LLMService {
  /**
   * Translate a string to a target language using LLM (e.g., 'hindi')
   */
  static async translate(text: string, language: string): Promise<string> {
    try {
      const userPrompt = `Translate the following string to ${language} (use the native script of the language): "${text}". Only return the translated string, no explanation.`;
      const { data, error } = await supabase.functions.invoke('call-llm', {
        body: {
          userPrompt,
          model: 'gpt-4o-mini'
        }
      });
      if (error) throw error;
      // The LLM should return just the translated string
      const translated = (data.reply || '').trim().replace(/^"|"$/g, '');
      return translated;
    } catch (error) {
      logger.error('LLM translation failed', { error, text, language });
      throw error;
    }
  }

  /**
   * Enhance text using AI - fix spelling, formatting, and improve quality
   */
  static async enhanceText(text: string, customPrompt?: string): Promise<string> {
    try {
      const defaultPrompt = `Enhance the following text by fixing spelling errors, improving formatting, and making it more professional and clear. Only return the enhanced text, no explanation or quotes: "${text}"`;
      const userPrompt = customPrompt
        ? `${customPrompt}: "${text}"`
        : defaultPrompt;
      const { data, error } = await supabase.functions.invoke('call-llm', {
        body: {
          userPrompt,
          model: 'gpt-4o-mini'
        }
      });
      if (error) throw error;
      // The LLM should return just the enhanced text
      const enhanced = (data.reply || '').trim().replace(/^"|"$/g, '');
      return enhanced;
    } catch (error) {
      logger.error('LLM text enhancement failed', { error, text });
      throw error;
    }
  }

  /**
   * Analyze images and generate content based on them
   */
  static async analyzeImages(
    imageUrls: string[],
    prompt: string,
    model: string = 'gpt-4o'
  ): Promise<string> {
    try {
      // Convert image URLs to base64
      const imageBase64Promises = imageUrls.map(async (url) => {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => {
              if (typeof reader.result === 'string') {
                // Remove the data:image/* prefix from base64 string
                const base64 = reader.result.split(',')[1];
                if (base64) {
                  resolve(base64);
                } else {
                  reject(new Error('Failed to convert image to base64'));
                }
              } else {
                reject(new Error('Failed to convert image to base64'));
              }
            };
            reader.onerror = error => reject(error);
          });
        } catch (error) {
          logger.error('Failed to fetch image for base64 conversion', { error, url });
          throw error;
        }
      });

      const imageBase64Array = await Promise.all(imageBase64Promises);

      // Use the first image (the call-llm function currently supports only one image)
      const imageBase64 = imageBase64Array[0];

      const { data, error } = await supabase.functions.invoke('call-llm', {
        body: {
          userPrompt: prompt,
          imageBase64: imageBase64,
          model: model
        }
      });
      if (error) throw error;
      return (data.reply || '').trim();
    } catch (error) {
      logger.error('LLM image analysis failed', { error, imageUrls, prompt });
      throw error;
    }
  }

  /**
   * Conversational AI with history and context
   */
  static async chatWithImages(
    imageUrls: string[],
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>,
    model: string = 'gpt-4o'
  ): Promise<string> {
    try {
      // Convert image URLs to base64 (only for the first message with images)
      let imageBase64: string | undefined;
      if (imageUrls.length > 0 && imageUrls[0]) {
        try {
          const response = await fetch(imageUrls[0]);
          const blob = await response.blob();
          imageBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => {
              if (typeof reader.result === 'string') {
                const base64 = reader.result.split(',')[1];
                if (base64) {
                  resolve(base64);
                } else {
                  reject(new Error('Failed to convert image to base64'));
                }
              } else {
                reject(new Error('Failed to convert image to base64'));
              }
            };
            reader.onerror = error => reject(error);
          });
        } catch (error) {
          logger.error('Failed to fetch image for base64 conversion', { error, url: imageUrls[0] });
        }
      }

      // Build conversation history
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // For the first user message with image, combine text and image
      const lastMessage = conversationHistory[conversationHistory.length - 1];
      if (imageBase64 && lastMessage && lastMessage.role === 'user') {
        // The edge function will handle the image in the user prompt
        const { data, error } = await supabase.functions.invoke('call-llm', {
          body: {
            userPrompt: lastMessage.content,
            systemPrompt: conversationHistory.find(m => m.role === 'system')?.content,
            imageBase64: imageBase64,
            model: model
          }
        });
        if (error) throw error;
        return (data.reply || '').trim();
      } else {
        // For text-only conversations, we need to update the edge function to support messages array
        // For now, use the last message as userPrompt
        const systemMsg = conversationHistory.find(m => m.role === 'system');
        const userMsg = lastMessage?.content || '';
        
        const { data, error } = await supabase.functions.invoke('call-llm', {
          body: {
            userPrompt: userMsg,
            systemPrompt: systemMsg?.content,
            model: model
          }
        });
        if (error) throw error;
        return (data.reply || '').trim();
      }
    } catch (error) {
      logger.error('LLM chat failed', { error, imageUrls, messages });
      throw error;
    }
  }
} 